import { readFile, writeFile, mkdir } from 'node:fs/promises';
import { join, dirname } from 'node:path';
import { createHash } from 'node:crypto';
import type { CheckResult } from '../types/index.js';

export interface BaselineEntry {
  ruleId: string;
  file?: string;
  line?: number;
  fingerprint: string;
  message: string;
  createdAt: string;
}

export interface Baseline {
  version: string;
  createdAt: string;
  updatedAt: string;
  entries: BaselineEntry[];
}

const BASELINE_VERSION = '1.0';
const DEFAULT_BASELINE_PATH = '.devrail/baseline.json';

export function createFingerprint(result: CheckResult): string {
  const content = [result.ruleId, result.file ?? '', result.message].join('|');
  return createHash('sha256').update(content).digest('hex').slice(0, 16);
}

export async function loadBaseline(cwd: string, path?: string): Promise<Baseline | null> {
  const baselinePath = join(cwd, path ?? DEFAULT_BASELINE_PATH);

  try {
    const content = await readFile(baselinePath, 'utf-8');
    return JSON.parse(content) as Baseline;
  } catch {
    return null;
  }
}

export async function saveBaseline(cwd: string, baseline: Baseline, path?: string): Promise<void> {
  const baselinePath = join(cwd, path ?? DEFAULT_BASELINE_PATH);

  await mkdir(dirname(baselinePath), { recursive: true });
  await writeFile(baselinePath, JSON.stringify(baseline, null, 2), 'utf-8');
}

export function createBaseline(results: CheckResult[]): Baseline {
  const now = new Date().toISOString();

  return {
    version: BASELINE_VERSION,
    createdAt: now,
    updatedAt: now,
    entries: results.map((r) => ({
      ruleId: r.ruleId,
      file: r.file,
      line: r.line,
      fingerprint: createFingerprint(r),
      message: r.message,
      createdAt: now,
    })),
  };
}

export function filterNewIssues(results: CheckResult[], baseline: Baseline | null): CheckResult[] {
  if (!baseline) {
    return results;
  }

  const baselineFingerprints = new Set(baseline.entries.map((e) => e.fingerprint));

  return results.filter((result) => {
    const fingerprint = createFingerprint(result);
    return !baselineFingerprints.has(fingerprint);
  });
}

export function getBaselinedIssues(results: CheckResult[], baseline: Baseline | null): CheckResult[] {
  if (!baseline) {
    return [];
  }

  const baselineFingerprints = new Set(baseline.entries.map((e) => e.fingerprint));

  return results.filter((result) => {
    const fingerprint = createFingerprint(result);
    return baselineFingerprints.has(fingerprint);
  });
}

export interface BaselineStats {
  total: number;
  new: number;
  baselined: number;
  fixed: number;
}

export function getBaselineStats(
  results: CheckResult[],
  baseline: Baseline | null
): BaselineStats {
  if (!baseline) {
    return {
      total: results.length,
      new: results.length,
      baselined: 0,
      fixed: 0,
    };
  }

  const currentFingerprints = new Set(results.map((r) => createFingerprint(r)));
  const baselineFingerprints = new Set(baseline.entries.map((e) => e.fingerprint));

  const newIssues = results.filter((r) => !baselineFingerprints.has(createFingerprint(r)));
  const baselinedIssues = results.filter((r) => baselineFingerprints.has(createFingerprint(r)));
  const fixedIssues = baseline.entries.filter((e) => !currentFingerprints.has(e.fingerprint));

  return {
    total: results.length,
    new: newIssues.length,
    baselined: baselinedIssues.length,
    fixed: fixedIssues.length,
  };
}

export function updateBaseline(baseline: Baseline, results: CheckResult[]): Baseline {
  const now = new Date().toISOString();
  const currentFingerprints = new Set(results.map((r) => createFingerprint(r)));

  // Keep only entries that still exist
  const existingEntries = baseline.entries.filter((e) => currentFingerprints.has(e.fingerprint));

  // Add new entries
  const existingFingerprints = new Set(existingEntries.map((e) => e.fingerprint));
  const newEntries = results
    .filter((r) => !existingFingerprints.has(createFingerprint(r)))
    .map((r) => ({
      ruleId: r.ruleId,
      file: r.file,
      line: r.line,
      fingerprint: createFingerprint(r),
      message: r.message,
      createdAt: now,
    }));

  return {
    ...baseline,
    updatedAt: now,
    entries: [...existingEntries, ...newEntries],
  };
}
